// Mòdul "Planificació General" — tipus compartits pel frontend. Independent
// del Gantt de Taller/Planificacio.tsx i del Cronograma de l'expedient.

export type PlanningResourceType = 'PERSONA' | 'VEHICLE' | 'MAQUINA' | 'EXTERN';

export interface PlanningResource {
  id: string;
  type: PlanningResourceType;
  // Id de l'Employee/Equipment/Supplier real d'origen. Null/undefined nomes
  // en recursos antics creats abans d'aquest vincle.
  sourceId?: string | null;
  name: string;
  profile?: string;
  dailyHours: number;
  workingDays: string;
  active: number;
  notes?: string;
}

// Element real (empleat/equip/proveïdor) disponible per vincular com a
// PlanningResource — retornat per GET /planning/resources/options.
export interface PlanningResourceOption {
  id: string;
  label: string;
  sublabel?: string;
  grup?: string;
}

export type PlanningCalendarExceptionType = 'FESTIU' | 'VACANCES' | 'BAIXA' | 'MANTENIMENT';

export interface PlanningResourceCalendarException {
  id: string;
  resourceId: string;
  type: PlanningCalendarExceptionType;
  startDate: string;
  endDate: string;
  note?: string;
}

export type PlanningPhaseStatus = 'PLANIFICADA' | 'EN_CURS' | 'COMPLETADA' | 'CANCELLADA';
export type PlanningDependencyOffsetType = 'CALENDAR' | 'WORKING';

export interface PlanningPhaseSegment {
  id: string;
  startDate: string;
  endDate: string;
}

export interface PlanningPhase {
  id: string;
  expedientCode?: string;
  expedientLabel?: string;
  chapter?: string | null;
  name: string;
  // Opcionals: una fase pot existir sense cap data encara (equivalent al
  // botó "+ Planificar" d'una línia del Cronograma).
  startDate?: string | null;
  endDate?: string | null;
  segments?: PlanningPhaseSegment[] | null;
  baselineStartDate?: string;
  baselineEndDate?: string;
  plannedHours: number;
  status: PlanningPhaseStatus;
  dependsOnPhaseId?: string | null;
  dependencyOffsetDays: number;
  dependencyOffsetType: PlanningDependencyOffsetType;
  isFixedDate: number;
  createdFromTemplateId?: string | null;
  notes?: string;
}

export interface PlanningResourceNeed {
  id: string;
  phaseId: string;
  profile: string;
  quantity: number;
  dedicationHours?: number;
  startDate?: string;
  endDate?: string;
}

export type PlanningAssignmentStatus = 'PROVISIONAL' | 'CONFIRMADA';

export interface PlanningAssignment {
  id: string;
  resourceId: string;
  phaseId: string;
  resourceNeedId?: string | null;
  startDate: string;
  endDate: string;
  // Trams no consecutius (opcional) — mateix patró que PlanningPhase.segments.
  segments?: PlanningPhaseSegment[] | null;
  startTime?: string | null;
  endTime?: string | null;
  hours?: number;
  status: PlanningAssignmentStatus;
  confirmedAt?: string | null;
  actualStartAt?: string | null;
  actualEndAt?: string | null;
  actualHours?: number;
  conflictOverride: number;
  conflictOverrideReason?: string;
}

export interface PlanningConflict {
  type: 'ASSIGNMENT_OVERLAP' | 'RESOURCE_UNAVAILABLE' | 'DEPENDENCY_VIOLATION';
  message: string;
  conflictingAssignmentId?: string;
}

export interface PlanningPhaseTemplateItem {
  id: string;
  templateId: string;
  name: string;
  orderIndex: number;
  durationDays: number;
  plannedHours: number;
  dependsOnItemId?: string | null;
  dependencyOffsetDays: number;
  dependencyOffsetType: PlanningDependencyOffsetType;
}

export interface PlanningPhaseTemplate {
  id: string;
  name: string;
  description?: string;
  items?: PlanningPhaseTemplateItem[];
}

export const RESOURCE_TYPE_LABEL: Record<PlanningResourceType, string> = {
  PERSONA: 'Persona',
  VEHICLE: 'Vehicle',
  MAQUINA: 'Màquina',
  EXTERN: 'Extern',
};

export const PHASE_STATUS_LABEL: Record<PlanningPhaseStatus, string> = {
  PLANIFICADA: 'Planificada',
  EN_CURS: 'En curs',
  COMPLETADA: 'Completada',
  CANCELLADA: 'Cancel·lada',
};

export const CALENDAR_EXCEPTION_LABEL: Record<PlanningCalendarExceptionType, string> = {
  FESTIU: 'Festiu',
  VACANCES: 'Vacances',
  BAIXA: 'Baixa',
  MANTENIMENT: 'Manteniment',
};
